import { Stack, Typography } from '@mui/material';
import { useWatch } from 'react-hook-form';

import { FormAwareCheckbox } from '@/components/form/formAwareInputs/FormAwareCheckbox';
import { MenuItem } from '@/components/poppers/MenuPopper/MenuItem';
import { useFormContext } from '@/components/react-hook-form';
import { ContextualHelpTooltip } from '@/modules/content/components/ContextualHelpTooltip';
import { ResiduaryContextualHelp } from '@/modules/dispositiveProvisions/DispositiveProvisionsFormV2/components/contextMenu/ResiduaryContextualHelp';
import { COLORS } from '@/styles/tokens/colors';

import {
  BENEFICIARY_DESIGNATIONS_FORM_NAMESPACE,
  BeneficiaryDesignationsFormShape,
} from '../BeneficiaryDesignationsForm.types';
import { useBdgResiduaryPaysAllTaxes } from '../hooks/useBdgResiduaryPaysAllTaxes';

interface ToggleBdgResiduaryPaysAllTaxesMenuItemProps {
  tier: 'primary' | 'contingent';
}

/**
 * BDG analog of the DP form's `ToggleResiduaryPaysTaxesMenuItem`. Toggles the
 * per-tier `residuaryPaysAllTaxes` flag; `useToggleBdgResiduaryPaysAllTaxes`
 * (mounted in the tier section) materializes each recipient's `bearsTax` when
 * the flag flips.
 *
 * Unlike the DP version there is no `isNonTaxableSource` gate: BDG sources are
 * always retirement/insurance accounts, which are never non-taxable.
 */
export function ToggleBdgResiduaryPaysAllTaxesMenuItem({
  tier,
}: ToggleBdgResiduaryPaysAllTaxesMenuItemProps) {
  const { control } = useFormContext<BeneficiaryDesignationsFormShape>();
  const { residuaryPaysAllTaxesFieldName } = useBdgResiduaryPaysAllTaxes(tier);

  const recipients = useWatch({
    control,
    name: `${BENEFICIARY_DESIGNATIONS_FORM_NAMESPACE}.${tier}.recipients`,
    defaultValue: [],
  });

  // Self-hide when the tier has no recipients (the flag only governs tax for
  // existing recipients), matching the sibling ToggleTransferInKindMenuItem /
  // ToggleGrowAllByInflationMenuItem.
  if (recipients.length === 0) {
    return null;
  }

  return (
    <MenuItem
      key="residuary-taxes-toggle"
      label={
        <Stack direction="row" alignItems="center" spacing={1}>
          <FormAwareCheckbox<BeneficiaryDesignationsFormShape>
            fieldName={residuaryPaysAllTaxesFieldName}
            control={control}
            label=""
          />
          <Typography variant="body-xs" color={COLORS.TEXT.PRIMARY}>
            All taxes paid out of residuary
          </Typography>
          <ContextualHelpTooltip>
            <ResiduaryContextualHelp />
          </ContextualHelpTooltip>
        </Stack>
      }
      onClick={(e) => {
        e.stopPropagation();
      }}
    />
  );
}
